import { t as tr } from '../i18n.js'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { api, ApiError, type ApprovalPeriod } from '../api.js'
import { fmtMinutes } from '../time.js'
import { fmt } from './Account.js'

export const STATE_LABEL: Record<string, string> = { open: 'not submitted', submitted: 'awaiting approval', approved: 'approved', rejected: 'sent back' }

/** My timesheets: one row per project-week; submit / withdraw. */
export function TimesheetsPage() {
  const qc = useQueryClient()
  const [weeks, setWeeks] = useState(8)
  const q = useQuery({ queryKey: ['approvals', 'mine', weeks], queryFn: () => api<{ periods: ApprovalPeriod[] }>(`/approvals/mine?weeksBack=${weeks}`) })
  const [err, setErr] = useState<string | null>(null)
  const invalidate = () => { void qc.invalidateQueries({ queryKey: ['approvals'] }); void qc.invalidateQueries({ queryKey: ['dashboard'] }) }
  const submit = useMutation({ mutationFn: (b: { projectId: string; weekStart: string; note?: string | null }) => api('/approvals/submit', { method: 'POST', json: b }), onSuccess: invalidate, onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not submit') })
  const withdraw = useMutation({ mutationFn: (b: { projectId: string; weekStart: string }) => api('/approvals/withdraw', { method: 'POST', json: b }), onSuccess: invalidate, onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not withdraw') })
  const rows = q.data?.periods ?? []
  return (
    <>
      <div className="row between"><div><h1>{tr("Timesheets")}</h1><ul className="help muted"><li>{tr("One submission per project per week, in each project's own timezone.")}</li><li>Approved weeks are locked; add corrections from <Link to="/time">{tr("Time")}</Link> and they go into a fresh submission.</li></ul></div>
        <label className="inline small">{tr("Show")} <select value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}><option value={4}>{tr("4 weeks")}</option><option value={8}>{tr("8 weeks")}</option><option value={13}>{tr("13 weeks")}</option><option value={26}>{tr("26 weeks")}</option></select></label></div>
      {err && <p className="bad small">{err}</p>}
      <section className="card">
        {rows.length === 0 && <p className="muted small">{tr("No time in this window.")}</p>}
        {rows.length > 0 && (
          <table className="table">
            <thead><tr><th>{tr("Week of")}</th><th>{tr("Project")}</th><th className="num">{tr("Hours")}</th><th>{tr("State")}</th><th>{tr("Notes")}</th><th></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.projectId}-${r.weekStart}`}>
                  <td className="mono">{r.weekStart}{r.isCurrentWeek && <span className="tag">{tr("this week")}</span>}</td>
                  <td><strong>{r.projectCode}</strong> <span className="muted small">{r.timezoneOfTruth}</span></td>
                  <td className="num mono">{fmtMinutes(r.minutes)}</td>
                  <td><StateChip state={r.state} forced={r.forceApproved} /></td>
                  <td className="small muted">
                    {r.state === 'rejected' && r.decisionNote && <span className="bad">{r.decisionNote}</span>}
                    {r.state === 'approved' && <>by {r.decidedByName} {fmt(r.decidedAt)}{r.decisionNote ? ` — ${r.decisionNote}` : ''}</>}
                    {r.state === 'submitted' && <>submitted {fmt(r.submittedAt)}{r.submitNote ? ` — ${r.submitNote}` : ''}</>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {(r.state === 'open' || r.state === 'rejected') && !r.isCurrentWeek && <button className="button primary" data-tip={tr("Send this project-week to the project administrator for approval; you can withdraw until they decide")} onClick={() => { const note = prompt('Note for the approver (optional)') ?? undefined; setErr(null); submit.mutate({ projectId: r.projectId, weekStart: r.weekStart, note: note || null }) }}>Submit</button>}
                    {(r.state === 'open' || r.state === 'rejected') && r.isCurrentWeek && <span className="small muted">{tr("closes Monday")}</span>}
                    {r.state === 'submitted' && <button className="link" data-tip={tr("Take the submission back to edit the week; resubmit when ready")} onClick={() => withdraw.mutate({ projectId: r.projectId, weekStart: r.weekStart })}>Withdraw</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  )
}

export function StateChip({ state, forced }: { state: string; forced?: boolean }) {
  return <span className={`chip st-${state}`}>{STATE_LABEL[state] ?? state}{forced ? ' (forced)' : ''}</span>
}
